import { createContext, useCallback, useContext, useMemo, useReducer, type Dispatch, type ReactNode } from "react";
import type { MailboxEvent } from "@onemileauto/shared-types";
import { retrySend as retrySendRequest, undoSend as undoSendRequest } from "../lib/api-client";

export interface OutboxToast {
  outboxId: string;
  threadId: string;
  messageId: string;
  scheduledSendAt: string;
  status: "pending" | "sent" | "failed" | "cancelled";
  subject?: string;
  toPreview?: string;
  error?: string;
}

export type OutboxToastAction =
  | { type: "seed"; outboxId: string; threadId: string; messageId: string; scheduledSendAt: string }
  | { type: "wsEvent"; event: MailboxEvent }
  | { type: "dismiss"; outboxId: string }
  | { type: "mark"; outboxId: string; status: OutboxToast["status"] };

function reducer(state: OutboxToast[], action: OutboxToastAction): OutboxToast[] {
  switch (action.type) {
    case "seed": {
      if (state.some((t) => t.outboxId === action.outboxId)) return state;
      return [
        ...state,
        {
          outboxId: action.outboxId,
          threadId: action.threadId,
          messageId: action.messageId,
          scheduledSendAt: action.scheduledSendAt,
          status: "pending",
        },
      ];
    }
    case "wsEvent": {
      const { event } = action;
      if (event.type === "message:sending") {
        const preview = {
          subject: event.message.subject,
          toPreview: event.message.to.map((p) => p.name || p.email).join(", "),
        };
        const existing = state.find((t) => t.outboxId === event.outboxId);
        if (existing) {
          return state.map((t) =>
            t.outboxId === event.outboxId
              ? { ...t, ...preview, status: "pending", scheduledSendAt: event.message.scheduledSendAt ?? t.scheduledSendAt }
              : t,
          );
        }
        return [
          ...state,
          {
            outboxId: event.outboxId,
            threadId: event.threadId,
            messageId: event.message.id,
            scheduledSendAt: event.message.scheduledSendAt ?? new Date().toISOString(),
            status: "pending",
            ...preview,
          },
        ];
      }
      if (event.type === "message:sent") {
        return state.map((t) => (t.outboxId === event.outboxId ? { ...t, status: "sent" } : t));
      }
      if (event.type === "message:cancelled") {
        return state.filter((t) => t.outboxId !== event.outboxId);
      }
      if (event.type === "message:failed") {
        return state.map((t) => (t.outboxId === event.outboxId ? { ...t, status: "failed", error: event.error } : t));
      }
      return state;
    }
    case "dismiss":
      return state.filter((t) => t.outboxId !== action.outboxId);
    case "mark":
      return state.map((t) => (t.outboxId === action.outboxId ? { ...t, status: action.status } : t));
    default:
      return state;
  }
}

interface OutboxToastContextValue {
  toasts: OutboxToast[];
  dismiss: (outboxId: string) => void;
  undo: (mailboxId: string, outboxId: string) => Promise<void>;
  retry: (mailboxId: string, outboxId: string) => Promise<void>;
}

const OutboxToastContext = createContext<OutboxToastContextValue | null>(null);
const OutboxToastDispatchContext = createContext<Dispatch<OutboxToastAction> | null>(null);

/**
 * Holds the "Sending in Ns... Undo" toast stack's state, driven by two
 * sources that both feed the same reducer keyed by outboxId: (1) the direct
 * POST /send response, seeded immediately so the affordance never depends
 * on the WebSocket round-tripping first, and (2) the mailbox's WebSocket
 * events (message:sending/sent/cancelled/failed — see
 * hooks/use-mailbox-socket.ts), which is what keeps the toast correct after
 * the compose sheet has already closed and carries the state transitions
 * (sent/failed) that only the server can tell us about. Mounted once at the
 * app root (see routes/root-layout.tsx) so it survives navigating between
 * the list and thread-detail panes mid-countdown.
 */
export function OutboxToastProvider({ children }: { children: ReactNode }) {
  const [toasts, dispatch] = useReducer(reducer, [] as OutboxToast[]);

  const dismiss = useCallback((outboxId: string) => dispatch({ type: "dismiss", outboxId }), []);

  const undo = useCallback(async (mailboxId: string, outboxId: string) => {
    const previousStatus = "pending" as const;
    dispatch({ type: "mark", outboxId, status: "cancelled" });
    try {
      const res = await undoSendRequest(mailboxId, outboxId);
      if (res.ok) {
        dispatch({ type: "dismiss", outboxId });
      } else {
        // Too late (the alarm won the race) or not found — the real
        // outcome (sent) will arrive via the WebSocket momentarily; revert
        // our optimistic guess in the meantime rather than showing
        // "cancelled" for a message that's actually still sending.
        dispatch({ type: "mark", outboxId, status: previousStatus });
      }
    } catch {
      dispatch({ type: "mark", outboxId, status: previousStatus });
    }
  }, []);

  const retry = useCallback(async (mailboxId: string, outboxId: string) => {
    try {
      const res = await retrySendRequest(mailboxId, outboxId);
      dispatch({ type: "seed", outboxId: res.outboxId, threadId: res.threadId, messageId: res.messageId, scheduledSendAt: res.scheduledSendAt });
      dispatch({ type: "mark", outboxId: res.outboxId, status: "pending" });
    } catch {
      dispatch({ type: "mark", outboxId, status: "failed" });
    }
  }, []);

  const value = useMemo(() => ({ toasts, dismiss, undo, retry }), [toasts, dismiss, undo, retry]);

  return (
    <OutboxToastContext.Provider value={value}>
      <OutboxToastDispatchContext.Provider value={dispatch}>{children}</OutboxToastDispatchContext.Provider>
    </OutboxToastContext.Provider>
  );
}

export function useOutboxToasts(): OutboxToastContextValue {
  const ctx = useContext(OutboxToastContext);
  if (!ctx) throw new Error("useOutboxToasts must be used within OutboxToastProvider");
  return ctx;
}

export function useOutboxToastDispatch(): Dispatch<OutboxToastAction> {
  const dispatch = useContext(OutboxToastDispatchContext);
  if (!dispatch) throw new Error("useOutboxToastDispatch must be used within OutboxToastProvider");
  return dispatch;
}
